import React from 'react';
import {
  Pagination,
  PaginationItem,
  Select,
  MenuItem,
  Typography,
  Box,
  SelectChangeEvent,
} from '@mui/material';
import { PaginationWrapper } from './LMA.style';
import { useSitecoreContext } from '@sitecore-jss/sitecore-jss-nextjs';

interface PaginationControlsProps {
  totalPages: number;
  currentPage: number;
  pageSize: number;
  totalItems: number;
  loading?: boolean;
  onPageChange: (event: React.ChangeEvent<unknown>, page: number) => void;
  onPageSizeChange?: (newPageSize: number) => void;
}

const PaginationControls: React.FC<PaginationControlsProps> = ({
  totalPages,
  currentPage,
  pageSize,
  totalItems,
  loading = false,
  onPageChange,
  onPageSizeChange,
}) => {
  const { sitecoreContext } = useSitecoreContext();
  const siteSettings: any = sitecoreContext?.siteSettings;

  const handleRowsPerPageChange = (event: SelectChangeEvent<number>) => {
    if (onPageSizeChange) {
      onPageSizeChange(Number(event.target.value));
    }
  };

  const rangeStart = totalItems === 0 ? 0 : (currentPage - 1) * pageSize + 1;
  const rangeEnd = Math.min(currentPage * pageSize, totalItems);

  return (
    <PaginationWrapper
      sx={{
        flexWrap: 'wrap',
        justifyContent: 'space-between',
        px: 1,
        py: 0.5,
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
        {siteSettings?.enableRowsPerPage?.value && (
          <>
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ fontSize: '13px', whiteSpace: 'nowrap' }}
            >
              Rows per page:
            </Typography>
            <Select
              value={pageSize}
              onChange={handleRowsPerPageChange}
              disabled={loading}
              size="small"
              variant="outlined"
              sx={{
                fontSize: '13px',
                height: '30px',
                minWidth: '64px',
                '& .MuiOutlinedInput-notchedOutline': {
                  borderColor: (theme: any) => theme.palette.neutral3?.main || '#E9E9E9',
                },
              }}
            >
              {(siteSettings?.perPageSetting?.value || '10,15,20')
                .split(',')
                .map((size: string) => (
                  <MenuItem key={size} value={size} sx={{ fontSize: '13px' }}>
                    {size}
                  </MenuItem>
                ))}
            </Select>
          </>
        )}
        <Typography
          variant="body2"
          color="text.secondary"
          sx={{ fontSize: '13px', whiteSpace: 'nowrap' }}
        >
          {totalItems === 0
            ? '0 records'
            : `${rangeStart}–${rangeEnd} of ${totalItems.toLocaleString()} records`}
        </Typography>
      </Box>

      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <Typography
          variant="body2"
          color="text.secondary"
          sx={{ fontSize: '13px', whiteSpace: 'nowrap' }}
        >
          Page {currentPage} of {totalPages}
        </Typography>
        <Pagination
          count={totalPages}
          page={currentPage}
          onChange={onPageChange}
          disabled={loading}
          shape="rounded"
          size="small"
          showFirstButton
          showLastButton
          renderItem={(item) => (
            <PaginationItem
              {...item}
              sx={{
                fontSize: '13px',
                minWidth: '30px',
                height: '30px',
                '&.Mui-selected': {
                  backgroundColor: (theme: any) => theme.palette.themeColor?.main || '#255E5E',
                  color: '#fff',
                  fontWeight: 700,
                  '&:hover': {
                    backgroundColor: (theme: any) => theme.palette.themeColor?.dark || '#1a4545',
                  },
                },
              }}
            />
          )}
        />
      </Box>
    </PaginationWrapper>
  );
};

export default PaginationControls;
